import React, { useState, useMemo, useRef } from 'react';
import groupBy from 'lodash/groupBy';
import type { EntryPointProps } from 'react-relay';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/new';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import ChevronDownIcon from '@atlaskit/icon/utility/migration/chevron-down';
import { Stack, xcss, Box, media } from '@atlaskit/primitives';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { useIsPlanEmbed } from '@atlassian/jira-portfolio-3-common/src/embed/index.tsx';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { usePathParam } from '@atlassian/react-resource-router';
import { MAX_ACTIVE_BOARDS, MAX_ACTIVE_BOARDS_OLD } from '../../common/constants.tsx';
import { getBoardUrl, getManageBoardSettingsUrl } from '../../common/utils.tsx';
import { useFetchIncrements } from '../../services/fetch-increments/index.tsx';
import type { Increment } from '../../services/fetch-increments/types.tsx';
import IncrementCreation from '../increment-forms/creation-modal/index.tsx';
import messages from './messages.tsx';
import type { Props, IncrementItemProps, BoardSwitcherDropdownProps } from './types.tsx';

const BoardItemOld = ({ board: { id, name }, currentId, analyticsEvent }: IncrementItemProps) => (
	<DropdownItem
		isSelected={id === currentId}
		key={`active-board-${id}`}
		href={getBoardUrl(id)}
		title={name}
		onClick={() => {
			fireUIAnalytics(analyticsEvent, 'switchProgramBoard');
		}}
		UNSAFE_shouldDisableRouterLink
	>
		{name}
	</DropdownItem>
);

const BoardItemNew = ({ board: { id, name }, currentId, analyticsEvent }: IncrementItemProps) => {
	const isEmbed = useIsPlanEmbed();

	return (
		<DropdownItem
			isSelected={id === currentId}
			key={`active-board-${id}`}
			href={getBoardUrl(id, isEmbed)}
			title={name}
			onClick={() => {
				fireUIAnalytics(analyticsEvent, 'switchProgramBoard');
			}}
			UNSAFE_shouldDisableRouterLink
		>
			{name}
		</DropdownItem>
	);
};

const BoardItem = componentWithFG('smart_links_for_plans', BoardItemNew, BoardItemOld);

export const BoardSwitcherDropdown = ({
	boardId,
	defaultOpen,
	onOpenCreateModal,
	triggerLabel,
	isReadOnly,
	returnFocusRef,
}: BoardSwitcherDropdownProps) => {
	const [planId] = usePathParam('planId');
	const [scenarioId] = usePathParam('scenarioId');
	const { increments, isLoading } = useFetchIncrements({ planId });
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const analyticsEvent = createAnalyticsEvent({
		action: 'clicked',
		actionSubject: 'dropdownItem',
	});

	const currentBoard = useMemo(
		() => increments?.find(({ id }) => id === boardId),
		[increments, boardId],
	);

	const groupedOptions = useMemo(() => groupBy(increments, 'status'), [increments]);

	const activeOptions = groupedOptions.ACTIVE;
	const pastOptions = groupedOptions.PAST;

	const triggerContent = triggerLabel ?? currentBoard?.name;

	const activeBoardLimit = fg('jpo_adjust_program_board_active_limit')
		? MAX_ACTIVE_BOARDS
		: MAX_ACTIVE_BOARDS_OLD;

	return (
		<DropdownMenu
			trigger={({ triggerRef, ...props }) => (
				<Box ref={triggerRef} xcss={triggerStyles}>
					<Button
						{...props}
						ref={returnFocusRef}
						isLoading={isLoading}
						iconAfter={ChevronDownIcon}
						testId="portfolio-3-plan-increment-common.ui.board-switcher.trigger-button"
					>
						{/* The LoadingButton takes on the width on the content while loading, so we need a placeholder to give it some width before the currentBoard is loaded */}
						{triggerContent ?? <Box xcss={placeholderStyles} />}
					</Button>
				</Box>
			)}
			returnFocusRef={returnFocusRef}
			shouldRenderToParent
			isLoading={isLoading}
			placement="bottom-start"
			defaultOpen={defaultOpen}
			{...(fg('jpo_adjust_program_board_active_limit') ? { shouldFlip: false } : {})}
		>
			<Stack
				xcss={fg('jpo_adjust_program_board_active_limit') ? boardGroupStyles : boardGroupStylesOld}
			>
				{activeOptions && activeOptions.length > 0 && (
					<DropdownItemGroup
						title={`${formatMessage(messages.activeGroupLabel)} (${activeOptions.length}/${activeBoardLimit})`}
					>
						{activeOptions.map((board) => (
							<BoardItem
								key={`active-board-${board.id}`}
								board={board}
								currentId={currentBoard?.id}
								analyticsEvent={analyticsEvent}
							/>
						))}
					</DropdownItemGroup>
				)}
				{pastOptions && pastOptions.length > 0 && (
					<DropdownItemGroup title={formatMessage(messages.pastGroupLabel)}>
						{pastOptions.map((board) => (
							<BoardItem
								key={`past-board-${board.id}`}
								board={board}
								currentId={currentBoard?.id}
								analyticsEvent={analyticsEvent}
							/>
						))}
					</DropdownItemGroup>
				)}
			</Stack>
			{!isReadOnly && (
				<DropdownItemGroup hasSeparator>
					<DropdownItem
						testId="portfolio-3-plan-increment-common.ui.board-switcher.create-button"
						isDisabled={activeOptions && activeOptions.length >= activeBoardLimit}
						onClick={() => {
							fireUIAnalytics(analyticsEvent, 'createProgramBoard');
							onOpenCreateModal();
						}}
					>
						{formatMessage(messages.createProgramBoard)}
					</DropdownItem>
					<DropdownItem
						testId="portfolio-3-plan-increment-common.ui.board-switcher.manage-button"
						href={getManageBoardSettingsUrl(planId, scenarioId)}
						title={formatMessage(messages.manageProgramBoardsTitle)}
						onClick={() => {
							fireUIAnalytics(analyticsEvent, 'manageProgramBoards');
						}}
						UNSAFE_shouldDisableRouterLink
					>
						{formatMessage(messages.manageProgramBoards)}
					</DropdownItem>
				</DropdownItemGroup>
			)}
		</DropdownMenu>
	);
};

const BoardSwitcher = ({ boardId, defaultOpen, triggerLabel, isReadOnly }: Props) => {
	const [isCreateBoardModalOpen, setIsCreateBoardModalOpen] = useState(false);

	const openCreateBoardModal = () => {
		setIsCreateBoardModalOpen(true);
	};

	const closeCreateBoardModal = () => {
		setIsCreateBoardModalOpen(false);
	};

	const onIncrementCreated = (newBoard: Increment) => {
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		window.location.replace(getBoardUrl(newBoard.id));
	};
	const returnFocusRef = useRef<HTMLButtonElement | null>(null);

	return (
		<>
			<IncrementCreation
				onIncrementCreated={onIncrementCreated}
				isOpen={isCreateBoardModalOpen}
				onClose={closeCreateBoardModal}
				returnFocusRef={returnFocusRef}
			/>
			<BoardSwitcherDropdown
				boardId={boardId}
				defaultOpen={defaultOpen}
				onOpenCreateModal={openCreateBoardModal}
				triggerLabel={triggerLabel}
				isReadOnly={isReadOnly}
				returnFocusRef={returnFocusRef}
			/>
		</>
	);
};

export default BoardSwitcher;

export const AsyncBoardSwitcher = ({ props }: EntryPointProps<{}, {}, Props, {}>) => (
	<BoardSwitcher {...props} />
);

const boardGroupStylesOld = xcss({
	maxHeight: '600px',
	overflow: 'auto',
	textAlign: 'left',
});

const boardGroupStyles = xcss({
	maxHeight: '400px',
	overflow: 'auto',
	textAlign: 'left',
});

const triggerStyles = xcss({
	marginLeft: 'space.200',
	maxWidth: '175px',
	[media.above.md]: {
		maxWidth: '240px',
	},
	[media.above.lg]: {
		maxWidth: '280px',
	},
});

const placeholderStyles = xcss({
	width: '80px',
});
